import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import axios from 'axios';
import { message } from 'antd';

const DownloadManagerContext = createContext(null);

/** Genera un id corto único para identificar cada descarga en la bandeja */
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/** Da formato legible a un tamaño en bytes: 1536 -> "1.5 KB" */
export const formatBytes = (bytes) => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** exp).toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
};

/** Da formato legible a una velocidad en bytes/seg: 2500000 -> "2.4 MB/s" */
const formatRate = (bytesPerSecond) => {
    if (!bytesPerSecond || bytesPerSecond <= 0) return '';
    return `${formatBytes(bytesPerSecond)}/s`;
};

/** Traduce un error de axios a un mensaje entendible para el usuario */
const describeDownloadError = (error) => {
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return null; // cancelado, no es error
    const status = error?.response?.status;
    if (status === 401) return 'Tu sesión expiró o fue cerrada. Vuelve a iniciar sesión para continuar.';
    if (status === 404) return 'El archivo o carpeta ya no existe en el servidor.';
    if (status === 413) return 'El archivo es demasiado grande para descargar.';
    if (!error?.response) return 'No se pudo conectar con el servidor. Verifica tu conexión.';
    return 'Ocurrió un error al descargar. Inténtalo de nuevo.';
};

/**
 * Provee un gestor de descargas global: cualquier componente puede pedir
 * `startDownload(...)` y el progreso se refleja en la bandeja flotante
 * (DownloadTray), incluso si el componente que la inició se desmonta
 * (p. ej. se cierra un Drawer) mientras la descarga sigue en curso.
 */
export const DownloadManagerProvider = ({ children }) => {
    const [downloads, setDownloads] = useState([]);
    const controllersRef = useRef(new Map());

    const patchDownload = useCallback((id, patch) => {
        setDownloads((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    }, []);

    const removeDownload = useCallback((id) => {
        controllersRef.current.delete(id);
        setDownloads((prev) => prev.filter((d) => d.id !== id));
    }, []);

    const clearFinished = useCallback(() => {
        setDownloads((prev) => prev.filter((d) => d.status === 'downloading'));
    }, []);

    const cancelDownload = useCallback((id) => {
        controllersRef.current.get(id)?.abort();
    }, []);

    /**
     * Inicia una descarga y la registra en la bandeja.
     * @param {Object} params
     * @param {string} params.url - endpoint completo (POST)
     * @param {FormData} params.formData - cuerpo de la petición
     * @param {string} params.key - identificador del recurso (p. ej. node.path), útil para que quien llama sepa si YA hay una descarga en curso para ese nodo
     * @param {string} params.name - nombre a mostrar en la bandeja
     * @param {'file'|'folder'} params.type
     * @param {(blob: Blob, headers: any) => void} params.onSuccess - recibe el blob para disparar la descarga en el navegador
     */
    const startDownload = useCallback(({ url, formData, key, name, type, onSuccess }) => {
        const id = generateId();
        const controller = new AbortController();
        controllersRef.current.set(id, controller);

        setDownloads((prev) => [
            ...prev,
            {
                id,
                key,
                name,
                type,
                status: 'downloading',
                loaded: 0,
                total: null,
                percent: null,
                rateLabel: '',
                errorMessage: null,
            },
        ]);

        axios
            .post(url, formData, {
                responseType: 'blob',
                signal: controller.signal,
                headers: { 'Content-Type': 'multipart/form-data' },
                onDownloadProgress: (evt) => {
                    const total = evt.total || null;
                    patchDownload(id, {
                        loaded: evt.loaded,
                        total,
                        percent: total ? Math.round((evt.loaded / total) * 100) : null,
                        rateLabel: formatRate(evt.rate),
                    });
                },
            })
            .then((res) => {
                patchDownload(id, { status: 'done', percent: 100 });
                onSuccess?.(res.data, res.headers);
                setTimeout(() => removeDownload(id), 6000);
            })
            .catch((error) => {
                const friendlyMessage = describeDownloadError(error);
                if (friendlyMessage === null) {
                    // Cancelado por el usuario
                    setTimeout(() => removeDownload(id), 1500);
                    patchDownload(id, { status: 'canceled' });
                    return;
                }
                patchDownload(id, { status: 'error', errorMessage: friendlyMessage });
                message.error(`${name}: ${friendlyMessage}`);
            });

        return id;
    }, [patchDownload, removeDownload]);

    const isDownloading = useCallback(
        (key) => downloads.some((d) => d.key === key && d.status === 'downloading'),
        [downloads]
    );

    const value = {
        downloads,
        startDownload,
        cancelDownload,
        removeDownload,
        clearFinished,
        isDownloading,
    };

    return (
        <DownloadManagerContext.Provider value={value}>
            {children}
        </DownloadManagerContext.Provider>
    );
};

export const useDownloadManager = () => {
    const ctx = useContext(DownloadManagerContext);
    if (!ctx) {
        throw new Error('useDownloadManager debe usarse dentro de un DownloadManagerProvider');
    }
    return ctx;
};
